import { t as uiText } from '../../i18n';
import { db } from '../../lib/firebase';
import { doc, setDoc, getDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import { DEFAULT_WORKSPACE_BASE_COLOR_KEY } from '../../theme/workspaceBaseColors';
import { resolveSignupRegion } from '../../utils/clientRegion';
import { SERIES_NCG } from '../plans/series';
import { HTML_PUBLISH_SETTINGS_ID } from '../../utils/htmlPublish';
import { validatePublishIdFormat } from './updatePublishId';

const DEFAULT_SERIES_ID = 'NCN';

function toOfferId(seriesId: string, planId: string): string {
  // offerId は外部連携や集計のキーになるため、安定・機械的に生成する
  // 例: NCN + free -> ncn:free
  return `${String(seriesId).toLowerCase()}:${String(planId).toLowerCase()}`;
}

interface CreateWorkspaceParams {
  id: string;
  name: string;
  createdBy: string;
  visibility: number;
  /**
   * 子以外では無視して free にする。子は親の planId を継承する。
   */
  planId?: string;
  /**
   * シリーズ（製品）識別子（例: NCN / NCG など）
   * 子ワークスペースの場合は親から継承される想定。
   */
  seriesId?: string;
  /** 企業/団体アカウントへの任意リンク。課金の必須条件ではない */
  companyId?: string;
  parentWorkspaceId?: string;
  /**
   * NCG の新規作成で必須。公開ページ URL の ID。
   * 子ワークスペースでは渡さない（親の公開設定をここでは作らない）。
   */
  publishId?: string;
}

interface CreateWorkspaceResult {
  success: boolean;
  error?: string;
  warning?: string;
}

async function setupNcgPublicPage(params: {
  workspaceId: string;
  publishId: string;
}): Promise<string | undefined> {
  const { workspaceId, publishId } = params;
  let warning: string | undefined;

  try {
    const now = serverTimestamp();
    await setDoc(doc(db, 'publicPageURL', publishId), {
      workspaceId,
      pageId: 'workspace-home',
      status: 'public',
      published: true,
      createdAt: now,
      updatedAt: now,
      publishedAt: now,
    });
  } catch (error) {
    console.error('NCG 公開URLの作成に失敗:', error);
    warning = uiText("The workspace was created. The public URL could not be created, so please try again from public settings.");
  }

  try {
    await setDoc(
      doc(db, `publicPages/${workspaceId}/settings`, 'widget'),
      { enabled: true, updatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (error) {
    console.error('NCG ウィジェット有効化に失敗:', error);
  }

  try {
    await setDoc(
      doc(db, `publicPages/${workspaceId}/settings`, HTML_PUBLISH_SETTINGS_ID),
      { enabled: false, updatedAt: serverTimestamp() },
      { merge: true },
    );
  } catch (error) {
    console.error('NCG ページWeb公開の初期OFFに失敗:', error);
  }

  return warning;
}

export const createWorkspace = async (
  params: CreateWorkspaceParams
): Promise<CreateWorkspaceResult> => {
  try {
    const { id, name, createdBy, visibility } = params;
    if (!id || id.length < 6) {
      return {
        success: false,
        error: uiText("Enter a workspace ID of at least 6 characters"),
      };
    }

    const requestedSeriesId = params.seriesId?.trim();
    const setupPublicPage = requestedSeriesId === SERIES_NCG && !params.parentWorkspaceId;
    const publishId = params.publishId?.trim() ?? '';
    if (setupPublicPage) {
      const formatCheck = validatePublishIdFormat(publishId);
      if (!formatCheck.valid) {
        return {
          success: false,
          error: formatCheck.error || uiText("publishID may contain only letters, numbers, and hyphens (-), and must be 3-50 characters"),
        };
      }
      const publicPageDoc = await getDoc(doc(db, 'publicPageURL', publishId));
      if (publicPageDoc.exists()) {
        return {
          success: false,
          error: uiText("This public ID is already in use."),
        };
      }
    }

    const workspaceRef = doc(db, 'workspaces', id);
    const workspaceDoc = await getDoc(workspaceRef);

    if (workspaceDoc.exists()) {
      return {
        success: false,
        error: uiText("This workspace ID is already in use"),
      };
    }

    // 親ワークスペースが指定されている場合、親から company / series / offer を継承する。
    // child はプランではないので planId に 'child' は書かない。
    // 子以外の新規作成は常に free。有料化は Checkout 後の Webhook だけが書く。
    let finalCompanyId = params.companyId;
    let finalSeriesId = params.seriesId;
    let finalPlanId: string | undefined;
    let parentOfferId: string | undefined;
    if (params.parentWorkspaceId) {
      try {
        const parentWorkspaceRef = doc(db, 'workspaces', params.parentWorkspaceId);
        const parentWorkspaceDoc = await getDoc(parentWorkspaceRef);
        if (parentWorkspaceDoc.exists()) {
          const parentData = parentWorkspaceDoc.data();
          if (!finalCompanyId) finalCompanyId = parentData?.companyId;
          if (!finalSeriesId) {
            finalSeriesId =
              parentData?.seriesId ||
              parentData?.productId ||
              parentData?.series ||
              undefined;
          }
          const parentPlan =
            typeof parentData?.planId === 'string' ? parentData.planId.trim() : '';
          if (parentPlan && parentPlan !== 'child') {
            finalPlanId = parentPlan;
          }
          if (typeof parentData?.offerId === 'string' && parentData.offerId.trim()) {
            parentOfferId = parentData.offerId.trim();
          }
        }
      } catch (error) {
        console.error('Failed to get parent workspace companyId:', error);
      }
    }

    // シリーズ未指定の新規作成は NCN。子は親のシリーズを優先する。
    if (!finalSeriesId) {
      finalSeriesId = DEFAULT_SERIES_ID;
    }
    if (!params.parentWorkspaceId) {
      finalPlanId = 'free';
    } else if (!finalPlanId) {
      finalPlanId = 'free';
    }

    const offerId =
      parentOfferId ||
      (finalSeriesId && finalPlanId ? toOfferId(finalSeriesId, finalPlanId) : undefined);

    // ワークスペース作成元の国を推定（GeoIP + ブラウザ TZ）。
    // 失敗時は country: null で続行し、作成自体はブロックしない。
    const region = await resolveSignupRegion();

    // ワークスペース本体を作成（membersフィールドは入れない）
    await setDoc(workspaceRef, {
      id,
      name,
      createdBy,
      visibility,
      ...(finalPlanId ? { planId: finalPlanId } : {}),
      ...(finalSeriesId ? { seriesId: finalSeriesId } : {}),
      ...(offerId ? { offerId } : {}),
      ...(setupPublicPage ? { publish: true, publishId } : {}),
      ...(finalCompanyId ? { companyId: finalCompanyId } : {}),
      ...(params.parentWorkspaceId ? { parentWorkspaceId: params.parentWorkspaceId } : {}),
      baseColorKey: DEFAULT_WORKSPACE_BASE_COLOR_KEY,
      creationCountry: region.country,
      creationCountryConfidence: region.confidence,
      creationTimezone: region.timezone,
      creationLanguage: region.language,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // サブコレクション: workspaces/{id}/members/{createdBy}
    const memberRef = doc(db, 'workspaces', id, 'members', createdBy);
    await setDoc(memberRef, {
      role: 'owner',   // 管理者
      status: 'active', // 有効
      joinedAt: serverTimestamp(),
    });

    // ユーザーの所属ワークスペースも記録（users/{uid}/workspaces/{id}）
    const userWorkspaceRef = doc(db, `users/${createdBy}/workspaces/${id}`);
    
    // 既存のユーザーのworkspacesサブコレクションから最大のorder値を取得
    const userWorkspacesRef = collection(db, `users/${createdBy}/workspaces`);
    const userWorkspacesSnapshot = await getDocs(userWorkspacesRef);
    let maxOrder = 0;
    
    userWorkspacesSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      if (data.order && data.order > maxOrder) {
        maxOrder = data.order;
      }
    });
    
    // 新しいorder値（最大値 + 1、または1）
    const newOrder = maxOrder + 1;
    
    await setDoc(userWorkspaceRef, {
      order: newOrder,
    });

    // companyに紐づいている場合、companies/{companyId}/workspaces/{workspaceId} に保存
    // 親ワークスペースから取得したcompanyIdも含む
    if (finalCompanyId) {
      const companyWorkspaceRef = doc(db, 'companies', finalCompanyId, 'workspaces', id);
      await setDoc(companyWorkspaceRef, {
        workspaceId: id,
        createdAt: serverTimestamp(),
      });
    }

    // 親ワークスペースに紐づいている場合、workspaces/{parentWorkspaceId}/childWorkspaces/{workspaceId} に保存
    if (params.parentWorkspaceId) {
      const parentWorkspaceRef = doc(db, 'workspaces', params.parentWorkspaceId, 'childWorkspaces', id);
      await setDoc(parentWorkspaceRef, {
        workspaceId: id,
        createdAt: serverTimestamp(),
      });
    }

    let warning: string | undefined;
    if (setupPublicPage) {
      warning = await setupNcgPublicPage({ workspaceId: id, publishId });
    }

    return warning ? { success: true, warning } : { success: true };
  } catch (error) {
    console.error('ワークスペース作成エラー:', error);
    return {
      success: false,
      error: uiText("Failed to create the workspace"),
    };
  }
};
